import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { DatePicker as DatePickerMUI, DateTimePicker as DateTimePickerMUI } from "@mui/x-date-pickers";
import CalendarIcon from "../Icons/Calendar";
import { IconSizes } from "../Icons/PropTypes/IconSizes";
import { useGetGerudoContainer } from "../hooks/useGetGerudoContainer";
import { Button, ButtonTypes } from "../Button";
import { useGlobalTranslations, useTranslations } from "../hooks/useTranslations";
import IconWrapper from "../Icons/IconWrapper";
import { DatepickerMode } from "./types";
import { sxPopperCustomDesign } from "./styles";
import { getDisabledDateAndTimeMUIProps, isDateRangePicker, propsAdapter, validateDate } from "./helpers";
import { translations } from "./translations";
import LegacyDateRangePicker from "./LegacyDateRangePicker";
const DatePicker = (props) => {
    var _a;
    if (isDateRangePicker(props)) {
        return React.createElement(LegacyDateRangePicker, Object.assign({}, props));
    }
    // Converts from moment to dayjs because MUI's datepicker isn't compatible with moment
    const adaptedProps = propsAdapter(props);
    const { ref, allowClear = false, inputReadOnly, showTime, showToday = false, className, disabled = inputReadOnly, disableFuture, disablePast, placeholder, placement = "bottom-start", format: formatProp, open = false, actions = [
        "accept",
        ...(showToday ? ["today"] : []),
        ...(allowClear ? ["clear"] : []),
        "cancel",
    ], name, picker = showTime ? DatepickerMode.DateTime : DatepickerMode.Date, prefix = React.createElement(DateIcon, null), suffix, autoFocus, required, value, defaultValue, minDate, maxDate, "aria-labelledby": ariaLabelledBy, "data-testid": dataTestId, onBlur: onBlurProp, onChange: onChangeProp, onOpenChange, renderExtraFooter, disabledDate, } = adaptedProps;
    const t = useTranslations(translations);
    const { ref: pickerRef, getGerudoContainer } = useGetGerudoContainer();
    const [isOpen, setIsOpen] = useState(open);
    const [innerValue, setInnerValue] = useState((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : null);
    useEffect(() => {
        // If no value is given and current datepicker value is default value
        // Don't overwrite default value
        if (!value && (innerValue === null || innerValue === void 0 ? void 0 : innerValue.valueOf()) === (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.valueOf())) {
            return;
        }
        if ((value === null || value === void 0 ? void 0 : value.valueOf()) !== (innerValue === null || innerValue === void 0 ? void 0 : innerValue.valueOf())) {
            setInnerValue(value !== null && value !== void 0 ? value : null);
        }
    }, [value === null || value === void 0 ? void 0 : value.valueOf()]);
    let Picker;
    let pickerProps;
    // For when props differ between different types of pickers
    if (picker === DatepickerMode.DateTime) {
        Picker = DateTimePickerMUI;
        pickerProps = Object.assign({ ampm: false }, getDisabledDateAndTimeMUIProps({ picker, disabledDate, timeSteps: { hours: 1, minutes: 5 } }));
    }
    else {
        Picker = DatePickerMUI;
        pickerProps = Object.assign({}, getDisabledDateAndTimeMUIProps({ picker, disabledDate }));
    }
    const icon = () => { var _a; return React.createElement(React.Fragment, null, (_a = suffix !== null && suffix !== void 0 ? suffix : prefix) !== null && _a !== void 0 ? _a : null); };
    const iconPosition = suffix ? "end" : "start";
    const views = [];
    let defaultFormat;
    let defaultPlaceholder;
    switch (picker) {
        case DatepickerMode.Year:
            views.push("year");
            defaultFormat = "YYYY";
            defaultPlaceholder = t("placeholders.year");
            break;
        case DatepickerMode.Month:
            views.push("year", "month");
            defaultFormat = "YYYY-MM";
            defaultPlaceholder = t("placeholders.month");
            break;
        case DatepickerMode.Date:
            views.push("year", "month", "day");
            defaultFormat = "YYYY-MM-DD";
            defaultPlaceholder = t("placeholders.date");
            break;
        case DatepickerMode.DateTime:
        default:
            views.push("year", "month", "day", "hours", "minutes");
            defaultFormat = "YYYY-MM-DD HH:mm";
            defaultPlaceholder = t("placeholders.dateTime");
    }
    const format = formatProp !== null && formatProp !== void 0 ? formatProp : defaultFormat;
    const onBlur = () => {
        var _a;
        // It is possible to manually type a date that is disabled in the picker
        // For now to prevent breaking existing logic we replicate the behaviour of
        // antd's DatePicker and wipe the field on blur when the value isn't allowed
        // TODO: How to handle this better and not break everything?
        if (innerValue &&
            !validateDate(innerValue, {
                minDate,
                maxDate,
                disablePast,
                disableFuture,
                disabledDate,
                format,
                useNativeDates: false,
            })) {
            setInnerValue((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : null);
        }
        onBlurProp === null || onBlurProp === void 0 ? void 0 : onBlurProp();
    };
    const onChange = (date) => {
        setInnerValue(date);
        // Invalid dates happen when manually typing
        if (date && !date.isValid())
            return;
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(date);
    };
    const openPopper = () => {
        if (disabled)
            return;
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true);
        setIsOpen(true);
    };
    const closePopper = () => {
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
        setIsOpen(false);
    };
    const onTextFieldKeyDown = (e) => {
        if (e.key === " ")
            openPopper();
    };
    return (React.createElement("div", { className: classNames("gerudo-datepicker", "gerudo-input", className), ref: pickerRef, "data-testid": dataTestId },
        React.createElement(Picker, Object.assign({}, pickerProps, { open: isOpen, value: innerValue, defaultValue: defaultValue, onChange: onChange, onOpen: openPopper, onClose: closePopper, ref: ref, format: format, disabled: disabled, disableFuture: disableFuture, disablePast: disablePast, minDate: minDate, maxDate: maxDate, minTime: minDate, maxTime: maxDate, disableIgnoringDatePartForTimeValidation: true, desktopModeMediaQuery: "@media (min-width: 0px)", showDaysOutsideCurrentMonth: true, views: views, slots: {
                openPickerIcon: icon,
                actionBar: (props) => (React.createElement(ActionBar, Object.assign({}, props, { picker: picker, closePopper: closePopper, renderExtraFooter: renderExtraFooter, actions: actions }))),
            }, slotProps: {
                actionBar: { actions },
                popper: {
                    sx: sxPopperCustomDesign,
                    container: getGerudoContainer,
                    placement: placement,
                },
                mobilePaper: {
                    sx: sxPopperCustomDesign,
                },
                textField: {
                    autoFocus,
                    required,
                    size: "small",
                    name,
                    onBlur,
                    onKeyDown: onTextFieldKeyDown,
                    placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : defaultPlaceholder,
                    "aria-labelledby": ariaLabelledBy,
                },
                inputAdornment: {
                    position: iconPosition,
                },
            } }))));
};
const ActionBar = ({ className, actions, picker, onAccept, onCancel, onClear, closePopper, onSetToday, renderExtraFooter, }) => {
    const tg = useGlobalTranslations();
    const t = useTranslations(translations);
    const actionsMap = {
        cancel: { label: tg("cancel"), type: ButtonTypes.Text, onClick: onCancel },
        clear: {
            icon: React.createElement(IconWrapper, { name: "Trash2", "aria-label": t("clear") }),
            type: ButtonTypes.DangerLite,
            onClick: onClear,
        },
        today: {
            label: picker === DatepickerMode.DateTime ? t("now") : t("today"),
            type: ButtonTypes.Secondary,
            onClick: onSetToday,
        },
        accept: { label: tg("ok"), type: ButtonTypes.Default, onClick: onAccept },
    };
    return (React.createElement("div", { className: className },
        renderExtraFooter && React.createElement("div", null, renderExtraFooter({ closePopper })),
        actions && actions.length > 0 && (React.createElement("div", { className: "gerudo-datepicker-actions" }, actions === null || actions === void 0 ? void 0 : actions.map((a) => {
            const { label, icon, onClick, type } = actionsMap[a];
            return (React.createElement(Button, { key: label, type: type, icon: icon, onClick: onClick }, label));
        })))));
};
const DateIcon = () => {
    return React.createElement(CalendarIcon, { color: "var(--palette-primary-600)", size: IconSizes.Medium });
};
DatePicker.displayName = "DatePicker";
export default DatePicker;
export { DatePicker };
//# sourceMappingURL=DatePicker.js.map